import { computed, effect, inject, Injectable, Signal, signal, untracked, WritableSignal } from '@angular/core';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { VenueConfigurationEntry, VenueConfigurations } from '@models/types/components/VenueConfigurations.type';
import { APP_CONFIG } from '@configuration/configuration';
import { ConfigurationService } from '@services/configuration.service';
import { UiService } from '@services/ui.service';


export const MAIN_CONFIGURATION_ID: string = 'main';


/**
 * Configuracion de venue activa en runtime.
 *
 * Seleccionar una configuracion NO escribe `Dvm.viewer`/`Dvm.minimap`: la capa DVM lee
 * `viewerConfig()`/`minimapConfig()` en su lugar. Escribirlas perderia los ids de la principal, y
 * publicar desde el admin los serializaria, reescribiendo el site.
 *
 * Root y no scoped: `DvmService` y `ViewerService` son root y no pueden inyectar `viewProviders`.
 */
@Injectable({
  providedIn: 'root'
})
export class VenueConfigurationsService {

  // CONFIGURATION
  private readonly config:        Signal<Configuration>        = inject(APP_CONFIG);
  private readonly dvmConfig:     Signal<Configuration['Dvm']> = computed((): Configuration['Dvm'] => this.config().Dvm);
  private readonly uiconfig:      Signal<UiConfiguration>      = inject(UiService).uiConfig;
  private readonly importPreview: Signal<boolean>              = inject(ConfigurationService).isImportPreviewMode;


  // COMPUTED CONFIGURATION
  private readonly settings:      Signal<VenueConfigurations>  = computed((): VenueConfigurations => this.uiconfig().VenueConfigurations);

  /** Texto del boton; '' = traduccion por defecto. Sin recorte: el boton trunca. `?.`: solo specs con APP_CONFIG sin validar. */
  public  readonly buttonLabel:   Signal<string>               = computed((): string => this.settings().buttonLabel?.trim() ?? '');

  /**
   * Entradas utilizables. El dialogo exige venueId/mapId/label, pero el schema zod es permisivo: una
   * config importada o editada a mano puede traerlas incompletas. La tool las marca "Incomplete".
   *
   * El `id` tambien: `.default('')` en el schema permite ids vacios o repetidos, y `override`
   * los resolveria todos a la primera entrada.
   */
  private readonly validExtras:   Signal<VenueConfigurationEntry[]> = computed((): VenueConfigurationEntry[] => {

    const seen: Set<string> = new Set<string>();

    return this.settings().data.filter((entry: VenueConfigurationEntry): boolean => {

      if(!entry.venueId?.trim() || !entry.mapId?.trim() || !entry.label?.trim()) return false;

      const id: string = entry.id?.trim() ?? '';

      // Un id `main` seria inseleccionable: `override` corta antes.
      if(!id || id === MAIN_CONFIGURATION_ID || seen.has(id)) return false;

      seen.add(id);

      return true;

    });

  });

  /**
   * `enabled` sin extras utilizables daria un panel de un solo boton: no se renderiza nada.
   *
   * Apagado en la preview de importacion: ahi el dialogo inicializa los viewers y `configChange` no
   * recarga el mapa, asi que elegir otra configuracion no cambiaria nada.
   */
  public  readonly enabled:       Signal<boolean>              = computed((): boolean => !this.importPreview() && this.settings().enabled && this.validExtras().length > 0);

  /**
   * `[main, ...data]`. Los ids de la principal se leen de `Dvm` en cada recomputo, nunca se copian:
   * una copia quedaria obsoleta en la primera edicion de "Venue and Map IDs".
   */
  public  readonly configurations: Signal<VenueConfigurationEntry[]> = computed((): VenueConfigurationEntry[] => {

    if(!this.enabled()) return [];

    const dvm: Configuration['Dvm'] = this.dvmConfig();

    return [
      {
        id:        MAIN_CONFIGURATION_ID,
        ...this.settings().main,
        venueId:   dvm.viewer.venueId,
        mapId:     dvm.viewer.mapId,
        minimapId: dvm.minimap.mapId
      },
      ...this.validExtras()
    ];

  });


  // STATE
  private readonly _selectedId:   WritableSignal<string>       = signal<string>(MAIN_CONFIGURATION_ID);
  public  readonly selectedId:    Signal<string>               = this._selectedId.asReadonly();


  /**
   * Entrada activa, o `null` si no hay override: principal, id desconocido o feature deshabilitada.
   * El ultimo caso importa: al desmarcar `enabled` el panel desaparece, y sin `null` el viewer
   * quedaria atrapado en la configuracion activa.
   */
  private readonly override:      Signal<VenueConfigurationEntry|null> = computed((): VenueConfigurationEntry|null => {

    const id: string = this._selectedId();

    if(!this.enabled() || id === MAIN_CONFIGURATION_ID) return null;

    // `validExtras`, no `data`: una entrada descartada degrada a la principal.
    return this.validExtras().find((entry: VenueConfigurationEntry): boolean => entry.id === id) ?? null;

  });

  public  readonly isOverridden:  Signal<boolean>              = computed((): boolean => this.override() !== null);

  /**
   * Id EFECTIVO, no `selectedId`: si la entrada activa se borra o queda incompleta, el mapa degrada
   * a la principal y el resaltado del panel debe seguir al mapa.
   */
  public  readonly activeId:      Signal<string>               = computed((): string => this.override()?.id ?? MAIN_CONFIGURATION_ID);


  // EFFECTIVE DVM CONFIGURATION
  /**
   * Lo que la capa DVM debe cargar. Con la principal devuelve la MISMA referencia que `Dvm.viewer`,
   * asi que los sites existentes no cambian. `is3d`, `isMultilevel`, `viewId` y `styles` se heredan
   * de la principal via spread: una entrada de `data` no los define.
   */
  public  readonly viewerConfig:  Signal<Configuration['Dvm']['viewer']> = computed((): Configuration['Dvm']['viewer'] => {

    const entry: VenueConfigurationEntry|null      = this.override(),
          base:  Configuration['Dvm']['viewer']    = this.dvmConfig().viewer;

    return entry ? { ...base, venueId: entry.venueId, mapId: entry.mapId } : base;

  });

  /** `minimapId: ''` significa "sin minimapa": se apaga en lugar de cargar un id vacio. */
  public  readonly minimapConfig: Signal<Configuration['Dvm']['minimap']> = computed((): Configuration['Dvm']['minimap'] => {

    const entry: VenueConfigurationEntry|null      = this.override(),
          base:  Configuration['Dvm']['minimap']   = this.dvmConfig().minimap;

    return entry
      ? { ...base, mapId: entry.minimapId, enabled: base.enabled && entry.minimapId !== '' }
      : base;

  });


  private readonly mainIds:       Signal<string>               = computed((): string => {
    const dvm: Configuration['Dvm'] = this.dvmConfig();
    return `${dvm.viewer.venueId}|${dvm.viewer.mapId}|${dvm.minimap.mapId}`;
  });

  private previousMainIds: string | null = null;

  /**
   * Si cambian los ids de la principal, el admin la esta editando y el override debe ceder: si no,
   * `configChange` compara contra el mapa ya cargado y la edicion queda enmascarada. `mainIds` es un
   * string memoizado, asi que escrituras no relacionadas no disparan esto. La primera ejecucion solo
   * registra el valor.
   */
  private readonly yieldToMainEdits = effect((): void => {

    const ids:     string  = this.mainIds();
    const changed: boolean = this.previousMainIds !== null && this.previousMainIds !== ids;

    this.previousMainIds = ids;

    if(changed) untracked((): void => this.resetToMain());

  });


  // METHODS
  /** Solo estado: `DvmService.configChange` observa `viewerConfig()` y dispara la recarga. */
  public select(id: string): void {
    this._selectedId.set(id);
  }

  /**
   * Vuelve a la principal. El servicio es root y sobrevive a la ruta, asi que sin esto la seleccion
   * se arrastraria de un site a otro en el admin.
   */
  public resetToMain(): void {
    this._selectedId.set(MAIN_CONFIGURATION_ID);
  }

}
